import { ChevronDown, ImageIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { formatMoney } from "../../lib/format";
import { tickerColors, type TickerAppearance } from "./tickerAppearance";

const tint = (percent: number): string =>
	`color-mix(in srgb, currentColor ${percent}%, transparent)`;

type TickerPreviewProps = {
	appearance: TickerAppearance;
	/** The gift, or for a code campaign the offer. Only a gift has a picture. */
	title: string;
	withImage: boolean;
	remainingMinor: number;
	progress: number;
	countdown: string | null;
};

/**
 * Deliberately hand-built rather than sharing the storefront stylesheet: the
 * merchant is looking at a shop that does not exist yet, so this only has to be
 * a faithful likeness, not the real thing.
 */
export const TickerPreview = ({
	appearance,
	title,
	withImage,
	remainingMinor,
	progress,
	countdown,
}: TickerPreviewProps) => {
	const colors = tickerColors(appearance);

	return (
		<div className="relative h-56 overflow-hidden rounded-md border border-line bg-[repeating-linear-gradient(45deg,var(--color-raised)_0px,var(--color-raised)_10px,var(--color-canvas)_10px,var(--color-canvas)_20px)]">
			<span className="absolute top-2 left-3 text-[10px] font-medium tracking-wide text-ink-faint uppercase">
				Preview
			</span>

			<div
				className={cn(
					"absolute bottom-4 w-[248px] rounded-xl p-3.5 shadow-lg",
					appearance.position === "bottom-left" ? "left-4" : "right-4",
				)}
				style={{ background: colors.background, color: colors.text }}
			>
				<ChevronDown className="absolute top-2 right-2 h-4 w-4 opacity-40" strokeWidth={2} />

				<p className="pr-5 text-[11px] font-semibold tracking-[0.04em] uppercase">
					{appearance.headline}
				</p>

				{countdown ? (
					<p className="mt-0.5 font-semibold tabular-nums">Ends in {countdown}</p>
				) : null}

				<div className="mt-2 flex items-center gap-2.5">
					{withImage ? (
						<span
							className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
							style={{ background: tint(10) }}
						>
							<ImageIcon className="h-4 w-4 opacity-50" strokeWidth={1.75} />
						</span>
					) : null}
					<span className="min-w-0">
						<span className="block truncate text-[13px] font-semibold">{title}</span>
						<span className="block text-xs opacity-70">
							{formatMoney((remainingMinor / 100).toFixed(2), "USD")} away
						</span>
					</span>
				</div>

				{appearance.showProgress ? (
					<span
						className="mt-2.5 block h-1 overflow-hidden rounded-full"
						style={{ background: tint(20) }}
					>
						<span
							className="block h-full rounded-full bg-current"
							style={{ width: `${Math.round(progress * 100)}%` }}
						/>
					</span>
				) : null}
			</div>
		</div>
	);
};
